import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { Empty, Loading, useAdminList } from "./shared";

const input = "mt-1 block w-full bg-transparent border-b border-pine/40 py-2 text-base focus:outline-none focus:border-brass transition-colors";
const BLANK = { couple: "", story: "", published: true };

const StoryForm = ({ open, onOpenChange, story, onSaved }) => {
  const [form, setForm] = useState(BLANK);
  const [initFor, setInitFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = story?.id || "new";
  if (open && initFor !== key) { setInitFor(key); setForm(story ? { couple: story.couple, story: story.story, published: story.published } : BLANK); }
  if (!open && initFor) setInitFor(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (story) await adminApi.put(`/admin/stories/${story.id}`, form);
      else await adminApi.post("/admin/stories", form);
      toast.success(form.published ? "Story saved and shown on your website." : "Story saved (hidden).");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="site bg-paper text-pine border-none rounded-2xl max-w-lg" data-testid="story-form-dialog">
        <DialogHeader>
          <DialogTitle className="dp text-3xl font-normal">{story ? "Edit story" : "Add a couple's story"}</DialogTitle>
          <DialogDescription className="text-moss">Only share a story with the couple's permission.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" data-testid="story-form">
          <label className="text-sm">How to name them (e.g. first names, branch)<input required value={form.couple} onChange={(e) => setForm({ ...form, couple: e.target.value })} className={input} data-testid="story-couple-input" /></label>
          <label className="text-sm">Their story, in their words<textarea required rows="5" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} className={input} data-testid="story-text-input" /></label>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="story-published-switch" />
            Show on the website
          </label>
          <button type="submit" disabled={saving} className="mt-2 rounded-full bg-pine text-sage py-4 disabled:opacity-70" data-testid="story-save-button">{saving ? "Saving..." : "Save story"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const StoriesTab = () => {
  const { items, reload } = useAdminList("/admin/stories");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const openForm = (s) => { setEditing(s); setOpen(true); };
  const remove = async (s) => {
    if (!window.confirm(`Delete the story from ${s.couple}?`)) return;
    try { await adminApi.delete(`/admin/stories/${s.id}`); toast.success("Story deleted."); reload(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <div>
      <button type="button" onClick={() => openForm(null)} className="mb-6 inline-flex items-center gap-2 rounded-full bg-pine text-sage px-6 py-3" data-testid="story-add-button"><Plus size={16} /> Add a story</button>
      {!items && <Loading />}
      {items && !items.length && <Empty testId="admin-stories-empty">No stories yet. Stories you add here appear in the "Stories of restored marriages" section.</Empty>}
      <ul className="grid gap-4" data-testid="admin-stories-list">
        {items?.map((s) => (
          <li key={s.id} className="bg-paper rounded-2xl p-6 grid sm:grid-cols-[1fr_auto] gap-4" data-testid={`admin-story-${s.id}`}>
            <div className="min-w-0">
              <p className="lb text-brass-ink flex items-center gap-2">{s.published ? <Eye size={14} /> : <EyeOff size={14} />} {s.published ? "Shown" : "Hidden"} · {s.couple}</p>
              <p className="mt-2 whitespace-pre-line">{s.story}</p>
            </div>
            <div className="flex sm:flex-col gap-4 items-start">
              <button type="button" onClick={() => openForm(s)} className="inline-flex items-center gap-1 text-sm hover:text-brass-ink" data-testid={`story-edit-${s.id}`}><Pencil size={14} /> Edit</button>
              <button type="button" onClick={() => remove(s)} className="inline-flex items-center gap-1 text-sm text-moss hover:text-red-800" data-testid={`story-delete-${s.id}`}><Trash2 size={14} /> Delete</button>
            </div>
          </li>
        ))}
      </ul>
      <StoryForm open={open} onOpenChange={setOpen} story={editing} onSaved={reload} />
    </div>
  );
};
